import React, { useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, updateData, deleteData } from "../../services/request"
import {
    UserCircleIcon,
    EnvelopeIcon,
    PhoneIcon,
    ArrowLeftIcon,
    TrashIcon,
} from "@heroicons/react/24/outline"

const AdminAttendeeDetails = () => {
    const { userId } = useParams()
    const navigate = useNavigate()
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const [submitting, setSubmitting] = useState(false)

    const fetchUser = async () => {
        setLoading(true)
        setError("")
        const url = `${getAPIBaseURL()}/v1/users`
        const params = { userId }
        try {
            const res = await getData(url, {}, params)
            if (res.result.status === 200) {
                const data = Array.isArray(res.result.data?.data) ? res.result.data.data[0] : res.result.data?.data
                setUser(data)
            } else {
                setError("User not found")
            }
        } catch (err) {
            setError("Failed to load user details")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchUser()
    }, [userId])

    const handleToggleStatus = async () => {
        if (!window.confirm(`Are you sure you want to ${user.status === 1 ? 'deactivate' : 'activate'} this user?`)) return

        setSubmitting(true)
        const url = `${getAPIBaseURL()}/v1/users/status`
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
        const params = new URLSearchParams()
        params.append('userId', userId)
        params.append('status', user.status === 1 ? '0' : '1')

        try {
            const res = await updateData(url, headers, params.toString())
            if (res.result.status === 200) {
                setSuccess(`User ${user.status === 1 ? 'deactivated' : 'activated'} successfully`)
                fetchUser()
            } else {
                setError("Status update failed")
            }
        } catch (err) {
            setError("Status update failed")
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async () => {
        if (!window.confirm("Are you sure? This cannot be undone.")) return

        setSubmitting(true)
        const url = `${getAPIBaseURL()}/v1/users`
        try {
            const res = await deleteData(url, {}, { userId })
            if (res.result.status === 200) {
                navigate('/admin/attendees')
            } else {
                setError("Delete failed")
            }
        } catch (err) {
            setError("Delete failed")
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) return <div className="text-center py-20">Loading...</div>
    if (!user) return <div className="text-center py-20">User not found</div>

    return (
        <div className="max-w-4xl mx-auto">
            <Link to="/admin/attendees" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
                <ArrowLeftIcon className="w-4 h-4" /> Back to Attendees
            </Link>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="bg-slate-50 px-8 py-6 border-b border-slate-200 flex justify-between items-start">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-xl">
                            {user.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">{user.name}</h1>
                            <p className="text-slate-500 text-sm">Joined {new Date(user.createdAt).toLocaleDateString()}</p>
                        </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${user.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                        {user.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                </div>

                <div className="p-8">
                    {error && <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl">{error}</div>}
                    {success && <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-xl">{success}</div>}

                    <div className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Contact Info</h3>
                                <div className="flex items-start gap-3">
                                    <EnvelopeIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-slate-900">Email Address</p>
                                        <p className="text-slate-600">{user.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <PhoneIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-slate-900">Phone Number</p>
                                        <p className="text-slate-600">{user.phoneNumber || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Account Stats</h3>
                                <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                                    <p className="text-xs text-slate-500 uppercase">User ID</p>
                                    <p className="font-mono text-sm text-slate-700 break-all">{user._id}</p>
                                </div>
                                {/* Placeholder for future specific stats like tickets bought */}
                            </div>
                        </div>

                        <div className="pt-8 border-t border-slate-100 flex flex-wrap gap-4">
                            <button onClick={handleToggleStatus} disabled={submitting} className={`flex items-center gap-2 px-4 py-2 border rounded-lg font-medium ${user.status === 1
                                    ? 'border-yellow-200 text-yellow-700 hover:bg-yellow-50'
                                    : 'border-green-200 text-green-700 hover:bg-green-50'
                                }`}>
                                {user.status === 1 ? 'Deactivate Account' : 'Activate Account'}
                            </button>
                            <button onClick={handleDelete} disabled={submitting} className="ml-auto flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50">
                                <TrashIcon className="w-4 h-4" /> Delete Account
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminAttendeeDetails
